export type IntegrationConnectionStatus = 'connected' | 'disconnected' | 'unknown';

export interface IntegrationConnectionCacheEntry {
  status: IntegrationConnectionStatus;
  lastCheckedAt: number | null;
  lastConfirmedAt: number | null;
  expiresAt: number | null;
  revalidationFailures: number;
  firstFailureAt: number | null;
  lastFailureAt: number | null;
}

export const CONNECTION_CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
export const CONNECTION_REVALIDATION_WINDOW_MS = 10 * 60 * 1000;
export const CONNECTION_REVALIDATION_MAX_FAILURES = 3;
export const CONNECTION_REVALIDATION_RETRY_INTERVAL_MS =
  CONNECTION_REVALIDATION_WINDOW_MS / (CONNECTION_REVALIDATION_MAX_FAILURES - 1);

const asNullableNumber = (value: unknown): number | null => {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const asNonNegativeInteger = (value: unknown): number => {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
};

const isConnectionStatus = (value: unknown): value is IntegrationConnectionStatus => {
  return value === 'connected' || value === 'disconnected' || value === 'unknown';
};

export const createUnknownIntegrationConnectionEntry = (): IntegrationConnectionCacheEntry => ({
  status: 'unknown',
  lastCheckedAt: null,
  lastConfirmedAt: null,
  expiresAt: null,
  revalidationFailures: 0,
  firstFailureAt: null,
  lastFailureAt: null,
});

export const createResolvedIntegrationConnectionEntry = (
  isConnected: boolean,
  now: number,
  previousEntry?: IntegrationConnectionCacheEntry
): IntegrationConnectionCacheEntry => {
  return {
    status: isConnected ? 'connected' : 'disconnected',
    lastCheckedAt: now,
    lastConfirmedAt: isConnected ? now : previousEntry?.lastConfirmedAt ?? null,
    expiresAt: now + CONNECTION_CACHE_DURATION_MS,
    revalidationFailures: 0,
    firstFailureAt: null,
    lastFailureAt: isConnected ? null : now,
  };
};

export const normalizeIntegrationConnectionEntry = (
  value: unknown
): IntegrationConnectionCacheEntry => {
  if (!value || typeof value !== 'object') {
    return createUnknownIntegrationConnectionEntry();
  }

  const candidate = value as Record<string, unknown>;

  return {
    status: isConnectionStatus(candidate.status) ? candidate.status : 'unknown',
    lastCheckedAt: asNullableNumber(candidate.lastCheckedAt),
    lastConfirmedAt: asNullableNumber(candidate.lastConfirmedAt),
    expiresAt: asNullableNumber(candidate.expiresAt),
    revalidationFailures: asNonNegativeInteger(candidate.revalidationFailures),
    firstFailureAt: asNullableNumber(candidate.firstFailureAt),
    lastFailureAt: asNullableNumber(candidate.lastFailureAt),
  };
};

export const hasResolvedIntegrationConnectionEntry = (
  entry: IntegrationConnectionCacheEntry | undefined
): boolean => {
  return !!entry && entry.status !== 'unknown';
};

export const isIntegrationConnectionEntryFresh = (
  entry: IntegrationConnectionCacheEntry | undefined,
  now: number
): boolean => {
  if (!entry || !hasResolvedIntegrationConnectionEntry(entry) || entry.expiresAt === null) {
    return false;
  }

  return now < entry.expiresAt;
};

export const getNextIntegrationConnectionRetryDelayMs = (
  entry: IntegrationConnectionCacheEntry | undefined,
  now: number
): number | null => {
  if (
    !entry ||
    entry.status !== 'connected' ||
    entry.revalidationFailures <= 0 ||
    entry.firstFailureAt === null
  ) {
    return null;
  }

  if (now - entry.firstFailureAt > CONNECTION_REVALIDATION_WINDOW_MS) {
    return 0;
  }

  if (entry.revalidationFailures >= CONNECTION_REVALIDATION_MAX_FAILURES) {
    return null;
  }

  const nextRetryAt =
    entry.firstFailureAt +
    entry.revalidationFailures * CONNECTION_REVALIDATION_RETRY_INTERVAL_MS;

  return Math.max(nextRetryAt - now, 0);
};

export const shouldRefreshIntegrationConnectionEntry = (
  entry: IntegrationConnectionCacheEntry | undefined,
  now: number,
  forceRefresh: boolean = false
): boolean => {
  if (forceRefresh) {
    return true;
  }

  if (!entry || entry.status === 'unknown') {
    return true;
  }

  const retryDelay = getNextIntegrationConnectionRetryDelayMs(entry, now);
  if (retryDelay !== null) {
    // Connected entries in a retry window refresh on their scheduled backoff,
    // even if their 24h freshness window has already expired.
    return retryDelay <= 0;
  }

  return !isIntegrationConnectionEntryFresh(entry, now);
};

export const applyIntegrationConnectionCheckResult = (
  entry: IntegrationConnectionCacheEntry | undefined,
  isConfirmedConnected: boolean,
  now: number
): IntegrationConnectionCacheEntry => {
  if (isConfirmedConnected) {
    return createResolvedIntegrationConnectionEntry(true, now, entry);
  }

  if (!entry || entry.status !== 'connected' || entry.lastConfirmedAt === null) {
    return createResolvedIntegrationConnectionEntry(false, now, entry);
  }

  const hasActiveFailureWindow =
    entry.firstFailureAt !== null &&
    now - entry.firstFailureAt <= CONNECTION_REVALIDATION_WINDOW_MS;

  const firstFailureAt = hasActiveFailureWindow ? entry.firstFailureAt : now;
  const revalidationFailures = hasActiveFailureWindow ? entry.revalidationFailures + 1 : 1;

  if (
    revalidationFailures >= CONNECTION_REVALIDATION_MAX_FAILURES &&
    firstFailureAt !== null &&
    now - firstFailureAt <= CONNECTION_REVALIDATION_WINDOW_MS
  ) {
    return createResolvedIntegrationConnectionEntry(false, now, entry);
  }

  return {
    ...entry,
    status: 'connected',
    lastCheckedAt: now,
    revalidationFailures,
    firstFailureAt,
    lastFailureAt: now,
  };
};

export const applyIntegrationConnectionCheckAbort = (
  entry: IntegrationConnectionCacheEntry | undefined,
  now: number
): IntegrationConnectionCacheEntry => {
  if (entry?.status === 'connected' && entry.lastConfirmedAt !== null) {
    return applyIntegrationConnectionCheckResult(entry, false, now);
  }

  return entry ? {...entry} : createUnknownIntegrationConnectionEntry();
};
